import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/common/Table';
import type { User } from '@/domain/User';
import { useEffect, useState } from 'react';

interface RestrictUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onSubmit: (userId: number, restricted: boolean) => void;
  isPending: boolean;
}

export function RestrictUserDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  isPending,
}: RestrictUserDialogProps) {
  const [restricted, setRestricted] = useState<boolean>(false);

  useEffect(() => {
    if (user && open) {
      setRestricted(!!user.restricted);
    }
  }, [user, open]);

  if (!user) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(user.id, restricted);
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title="Restrict User"
      maxWidth="4xl"
    >
      <form onSubmit={handleSubmit} className="mt-4 space-y-6">
        <Table>
          <TableBody>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2 font-semibold">
                User:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {user.name} {user.surname}
              </TableCell>
            </TableRow>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2 font-semibold">
                Email:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {user.email}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div className="space-y-4">
          <label className="group relative flex cursor-pointer items-center text-base select-none">
            <input
              type="checkbox"
              className="peer absolute h-0 w-0 cursor-pointer opacity-0"
              checked={restricted}
              onChange={(e) => setRestricted(e.target.checked)}
            />
            <span className="border-muted-foreground group-hover:bg-muted/20 peer-checked:bg-primary relative mr-2.5 h-5 w-5 shrink-0 rounded-sm border-2 transition-colors after:absolute after:top-px after:left-1.25 after:hidden after:h-3 after:w-1.5 after:rotate-45 after:border-r-2 after:border-b-2 after:border-white after:content-[''] peer-checked:after:block"></span>
            Restricted
          </label>
        </div>

        <div className="flex items-center justify-between gap-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={isPending}
          >
            {isPending ? 'Processing...' : 'Save Changes'}
          </Button>
          <RelationActionButton
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </RelationActionButton>
        </div>
      </form>
    </DialogWrapper>
  );
}
